import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material';
import mapboxgl from 'mapbox-gl';
import { useFetchLocationDetails } from '../mutations';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
const INITIAL_MAP_CENTER: [number, number] = [-1.895662, 52.489706];
const INITIAL_MAP_ZOOM = 11;
const UK_BOUNDS: [number, number, number, number] = [
  -10.8544921875, 49.82380908513249, 1.778564453125, 59.478568831926395,
];

interface UseMapboxOptions {
  location?: [number, number] | null;
  cooperativeGestures?: boolean;
  enableGeocoder?: boolean;
  onLocationChange?: (location: [number, number], address: string, postCode: string, what3_words: string) => void;
}

export const useMapbox = ({
  location,
  cooperativeGestures = false,
  enableGeocoder = false,
  onLocationChange,
}: UseMapboxOptions) => {

  const fetchMapService = useFetchLocationDetails();

  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const geocoder = useRef<MapboxGeocoder | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const theme = useTheme();

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Clean up controls and map
      geocoder.current?.clear();
      marker.current?.remove();
      map.current?.remove();
      geocoder.current = null;
      marker.current = null;
      map.current = null;
    };
  }, []);

  // Initialize geocoder
  const initializeGeocoder = () => {
    if (!enableGeocoder || !map.current) return;
    geocoder.current = new MapboxGeocoder({
      accessToken: MAPBOX_ACCESS_TOKEN,
      mapboxgl: mapboxgl as any,
      marker: false,
      placeholder: "Search for places",
      countries: "gb",
      bbox: UK_BOUNDS,
    });
    map.current.addControl(geocoder.current, "top-left");
    geocoder.current.on("result", async (e: { result: { center: [number, number]; place_name: any; }; }) => {
      const coordinates: [number, number] = e.result.center;
      const address = e.result.place_name;
      const geoCordinates: [number, number] = [coordinates[0], coordinates[1]];
      const response = await reverseGeocode(geoCordinates);
      onLocationChange?.(coordinates, address, response?.postcode, response?.what3_words);
    });
  };

  // Map initialization
  useEffect(() => {
    if (!mapContainer.current || map.current || !MAPBOX_ACCESS_TOKEN) {
      if (!MAPBOX_ACCESS_TOKEN) {
        setError('MapBox access token is not configured');
        setIsLoading(false);
      }
      return;
    }

    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

    try {
      const mapStyle =
        theme.palette.mode === 'dark'
          ? 'mapbox://styles/mapbox/dark-v11'
          : 'mapbox://styles/mapbox/standard';

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: mapStyle,
        center: location || INITIAL_MAP_CENTER,
        zoom: INITIAL_MAP_ZOOM,
        cooperativeGestures,
      });

      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      map.current.on('load', () => {
        setIsLoading(false);
        setIsMapLoaded(true);
        initializeGeocoder();

        // Add click handler for location selection when geocoder is enabled
        if (enableGeocoder && onLocationChange && map.current) {
          // Change cursor to indicate clickable map
          map.current.getCanvas().style.cursor = 'crosshair';

          map.current.on('click', async (e: { lngLat: { lng: number; lat: number; }; }) => {
            const coordinates: [number, number] = [e.lngLat.lng, e.lngLat.lat];
            const response = await reverseGeocode(coordinates);
            if (response) {
              geocoder.current?.setInput(response.adress);
            }
            onLocationChange(coordinates, response?.adress, response?.postcode, response?.what3_words);
          });
        }
      });

      map.current.on('error', (e: any) => {
        console.error('MapBox error:', e);
        setError('Failed to load map');
        setIsLoading(false);
      });
    } catch (err) {
      console.error('MapBox initialization error:', err);
      setError('Failed to initialize map');
      setIsLoading(false);
    }
  }, [theme.palette.mode, enableGeocoder, onLocationChange]);

  // Handle theme changes
  useEffect(() => {
    if (!map.current) return;

    setIsMapLoaded(false);
    const mapStyle =
      theme.palette.mode === 'dark'
        ? 'mapbox://styles/mapbox/dark-v11'
        : 'mapbox://styles/mapbox/standard';

    map.current.setStyle(mapStyle);
    map.current.once('styledata', () => {
      setIsMapLoaded(true);
    });
  }, [theme.palette.mode]);

  const reverseGeocode = async (coordinates: [number, number]) => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${coordinates[0]},${coordinates[1]}.json?access_token=${MAPBOX_ACCESS_TOKEN}`,
      );
      const params = {
        "type": "Point",
        "coordinates": [
          coordinates[0],
          coordinates[1]
        ]
      };
      const what3_words = await fetchMapService.mutateAsync(params);
      const data = await response.json();
      return { adress: data.features?.[0]?.place_name, postcode: data?.context?.[0]?.text || data?.features[1].text, what3_words: what3_words.data.word };
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      return undefined;
    }
  };

  // Handle marker updates
  useEffect(() => {
    if (!map.current || !isMapLoaded) return;

    // Remove existing marker
    marker.current?.remove();
    marker.current = null;

    // Create new marker if location exists
    if (location) {
      marker.current = new mapboxgl.Marker({
        scale: 1.2,
        color: theme.palette.primary.main,
        draggable: enableGeocoder,
      })
        .setLngLat(location)
        .addTo(map.current);

      // Add drag handler for draggable markers
      if (enableGeocoder && onLocationChange) {
        marker.current.on('dragend', async () => {
          if (!marker.current) return;
          const lngLat = marker.current.getLngLat();
          const coordinates: [number, number] = [lngLat.lng, lngLat.lat];
          const response = await reverseGeocode(coordinates);
          if (response) {
            geocoder.current?.setInput(response.adress);
          }
          onLocationChange(coordinates, response?.adress, response?.postcode, response?.what3_words);
        });
      }

      map.current.flyTo({ center: location, zoom: 15 });
    }
  }, [
    location,
    theme.palette.primary.main,
    enableGeocoder,
    onLocationChange,
    isMapLoaded,
  ]);

  return { mapContainer, isLoading, error, map };
};
